/**
 * The tab's View-as state for code outside React (M4S-5670,
 * docs/pdlc/cs-view-as-client.md §4).
 *
 * The axios, GraphQL and fetch backstops, the DANI send guard and
 * `recordViewAsAction` read this snapshot. Only the View-as lifecycle
 * (`view-as-lifecycle.ts`, driven by `SubsidiaryFilterProvider`) writes it. It
 * is module state, so it is per tab by construction.
 *
 * `sessionId` is set from the moment a stored session is being restored, so the
 * backstops refuse writes while the status is anything but `active`.
 */

export type ViewAsStatus = 'idle' | 'restoring' | 'starting' | 'active' | 'ending';

export interface ViewAsSnapshot {
    sessionId: string | null;
    orgId: number | null;
    sfAccountId: string | null;
    orgName: string | null;
    status: ViewAsStatus;
    /** Epoch ms of the last check the server passed. */
    lastVerifiedAt: number | null;
    /** Epoch ms the session's 12h TTL runs out. */
    expiresAt: number | null;
}

export const IDLE_VIEW_AS: ViewAsSnapshot = Object.freeze({
    sessionId: null,
    orgId: null,
    sfAccountId: null,
    orgName: null,
    status: 'idle',
    lastVerifiedAt: null,
    expiresAt: null,
}) as ViewAsSnapshot;

let snapshot: ViewAsSnapshot = IDLE_VIEW_AS;
const listeners = new Set<() => void>();

export function getViewAsSnapshot(): ViewAsSnapshot {
    return snapshot;
}

/** Lifecycle only. Replaces the snapshot and notifies subscribers. */
export function setViewAsSnapshot(next: ViewAsSnapshot): void {
    snapshot = next;
    for (const listener of [...listeners]) listener();
}

/** Lifecycle only. Shallow-merges into the snapshot. */
export function patchViewAsSnapshot(patch: Partial<ViewAsSnapshot>): void {
    setViewAsSnapshot({ ...snapshot, ...patch });
}

export function subscribeViewAs(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Whether the tab holds any View-as session id (restoring, starting, active or ending). */
export function hasViewAsSession(s: ViewAsSnapshot = snapshot): boolean {
    return s.sessionId != null;
}

/** Whether the tab is verified as viewing as a client right now. */
export function isViewAsActive(s: ViewAsSnapshot = snapshot): boolean {
    return s.sessionId != null && s.status === 'active';
}

/** Test seam. */
export function resetViewAsStoreForTests(): void {
    snapshot = IDLE_VIEW_AS;
    listeners.clear();
}
